<template>
    <span ref="root" class="reverse-geocoding" :aria-busy="loading ? 'true' : 'false'">
        <text-skeleton v-if="skeleton && loading" :lines="skeletonLines" />
        <template v-else>{{ address }}</template>
    </span>
</template>

<script>
import { ref } from 'vue'
import { useElementVisibility } from '@vueuse/core'
import TextSkeleton from '@/components/commons/TextSkeleton.vue'

export default {
    name: 'ReverseGeocoding',
    components: {
        TextSkeleton
    },
    props: {
        latLon: {
            type: Array,
            required: true
        },
        full: {
            type: Boolean,
            default: false
        },
        custom_fields: {
            type: Array,
            default: () => []
        },
        // Look the point up at city level and show the first of
        // `custom_fields` that has a value: "Verona"
        city_level: {
            type: Boolean,
            default: false
        },
        skeleton: {
            type: Boolean,
            default: false
        },
        skeletonLines: {
            type: Number,
            default: 2
        },
        // Ask for the address only once the element is scrolled into view. For
        // a long table that is the difference between a dozen requests and one
        // per row.
        lazy: {
            type: Boolean,
            default: false
        },
        skeletonMinDuration: {
            type: Number,
            default: 1000,
            validator: (value) => value >= 0
        }
    },
    setup() {
        const root = ref(null)

        return { root, isVisible: useElementVisibility(root) }
    },
    data() {
        return {
            address: '',
            loading: false,
            loadingStartedAt: 0,
            reverseTimer: null,
            reverseRequestId: 0
        }
    },
    computed: {
        effectiveSkeletonDuration() {
            return Math.max(
                this.skeleton ? this.skeletonMinDuration : 0,
                this.skeletonSimulationDelay
            )
        },
        skeletonSimulationDelay() {
            if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
                return 0
            }

            const routeDelay = this.$route?.query?.reverseSkeletonDelay
            const delay = Number(
                routeDelay ??
                    new URLSearchParams(window.location.search).get(
                        'reverseSkeletonDelay'
                    )
            )

            return Number.isFinite(delay) ? Math.min(Math.max(delay, 0), 10000) : 0
        },
        requestKey() {
            const lat = Number(this.latLon?.[0])
            const lng = Number(this.latLon?.[1])

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

            const composition =
                this.$store?.state?.config?.user?.addressComposition || []
            const compositionKey = composition
                .map((field) => {
                    if (typeof field === 'string') return field
                    return field?.id || field?.value || ''
                })
                .join('|')

            return [
                lat.toFixed(6),
                lng.toFixed(6),
                this.full ? 'full' : 'composed',
                this.custom_fields.join('|'),
                this.city_level ? 'city' : 'address',
                compositionKey
            ].join(':')
        }
    },
    watch: {
        requestKey: {
            immediate: true,
            handler() {
                this.scheduleReverse()
            }
        },
        isVisible() {
            if (this.lazy) this.scheduleReverse()
        }
    },
    methods: {
        scheduleReverse() {
            clearTimeout(this.reverseTimer)
            this.reverseRequestId++

            if (!this.requestKey) {
                this.address = ''
                this.loading = false
                return
            }

            // Lazy: nothing is asked for until the cell is on screen
            if (this.lazy && !this.isVisible) return

            const requestId = this.reverseRequestId
            const latLon = [this.latLon[0], this.latLon[1]]
            const options = this.getReverseOptions(latLon)
            const cachedAddress =
                typeof this.$getCachedReverseGeocoding === 'function'
                    ? this.$getCachedReverseGeocoding(options)
                    : null

            if (cachedAddress !== null && cachedAddress !== undefined) {
                this.setAddress(cachedAddress)
                this.loading = false
                return
            }

            this.address = ''
            this.loading = true
            this.loadingStartedAt = Date.now()

            // Avoid requests for cards that only cross the virtual-scroller buffer.
            this.reverseTimer = setTimeout(() => {
                this.reverseTimer = null
                this.reverse(latLon, requestId)
            }, 200)
        },
        async reverse(latLon, requestId) {
            try {
                const address = await this.$reverseGeocoding(this.getReverseOptions(latLon))

                if (requestId !== this.reverseRequestId || this._isDestroyed) return

                this.setAddress(address)
            } finally {
                if (requestId === this.reverseRequestId && !this._isDestroyed) {
                    const elapsed = Date.now() - this.loadingStartedAt
                    const remaining = this.effectiveSkeletonDuration - elapsed

                    if (remaining > 0) {
                        this.reverseTimer = setTimeout(() => {
                            if (
                                requestId === this.reverseRequestId &&
                                !this._isDestroyed
                            ) {
                                this.loading = false
                            }
                        }, remaining)
                    } else {
                        this.loading = false
                    }
                }
            }
        },
        getReverseOptions(latLon) {
            return {
                lat: latLon[0],
                lng: latLon[1],
                full: this.full,
                custom_fields: this.custom_fields.length ? this.custom_fields : null,
                city_level: this.city_level,
                raw: true
            }
        },
        setAddress(address) {
            this.applyAddress(address)

            // Lets the caller wait for the address before showing anything that
            // acts on it — the copy and maps buttons of a table cell.
            this.$emit('resolved', this.address)
        },
        applyAddress(address) {
            if (!address) {
                this.address = ''
            } else if (this.custom_fields.length) {
                this.address = address
            } else {
                this.address = this.full ? address : address.join(', ')
            }
        }
    },
    beforeDestroy() {
        clearTimeout(this.reverseTimer)
        this.reverseRequestId++
    }
}
</script>

<style scoped>
.reverse-geocoding {
    display: inline-block;
    width: 100%;
}
</style>
